import React from "react";
import ReactDOM from "react-dom/client";
import { QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter } from "react-router-dom";

import App from "./App";
import { ApiError } from "./api";
import "./styles.css";

// This screen is entirely polling and invalidation, which is why TanStack Query
// earns its place here rather than hand-rolled effects.
const queryClient = new QueryClient({
  // A session can expire while a tab sits open. When any query comes back 401,
  // re-ask whether we are signed in — App watches that answer and swaps to the
  // login screen. Without this the page would just fill with error panels while
  // the real cause went unstated.
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (error instanceof ApiError && error.status === 401) {
        if (query.queryKey[0] !== "session") {
          void queryClient.invalidateQueries({ queryKey: ["session"] });
        }
      }
    },
  }),
  defaultOptions: {
    queries: {
      // No polling by default, deliberately.
      //
      // This used to be 4000, inherited by every query that did not override
      // it — including "am I still signed in", asked fifteen times a minute for
      // an answer good for twelve hours. One idle tab cost 84 requests a
      // minute, and across a LAN to a busy GPU box that is not free.
      //
      // Almost nothing here changes unless someone presses a button, and every
      // button already invalidates what it affects (see useAction). So the
      // default is to ask once, and again when the tab is focused after being
      // away — which covers "somebody changed something from another machine".
      //
      // The two things that do move on their own — hardware readings and which
      // models are resident — opt in, and only when the server says to. See
      // useLive.
      refetchInterval: false,
      refetchOnWindowFocus: true,
      // A 503 means a runtime is down, which is a real answer, not a blip worth
      // retrying three times before telling anyone. A 401 is equally final.
      retry: (failureCount, error) =>
        error instanceof ApiError && error.status === 401 ? false : failureCount < 1,
      staleTime: 1000,
    },
  },
});

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>,
);
